import { all, call, put, select, take, takeEvery } from "redux-saga/effects";

import { ToastContent, getToastContent } from "constants/toasts";
import { ApiManager } from "services/api";
import TransactionSerializer from "services/api/transactions/transaction-serializer";
import { getItem, saveItem } from "services/session-storage";
import { ToastVariant, ToastsActions } from "store/features/toasts";
import { IActionWithPayload } from "store/helper";

import { CRUDAction, RequestAction } from "../actions";

import { TRANSACTION, TransactionActions } from "./actions";
import { TRANSACTION_ATTACHMENT_MAX_TOTAL_SIZE } from "./constants";
import {
  ICreateTransactionPayload,
  IMergeTransactionPayload,
  ITransactionDeleteAttachmentPayload,
  ITransactionUploadAttachmentPayload,
} from "./interfaces";
import { selectTransactions } from "./selectors";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* fetch(action: IActionWithPayload<string, { id: string }>) {
  const { id } = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.transactionApi.fetch, id);

  if (response.result) {
    yield put(
      TransactionActions.fetchSuccess({
        id: response.result.ID,
        transaction: TransactionSerializer.deserialize(response.result),
      })
    );
  } else {
    yield put(
      TransactionActions.fetchFailure({
        id,
        error: response.error,
      })
    );
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const parseAttachment = async (attachment: any, fileName?: string): Promise<Blob> => {
  if (attachment instanceof Blob) {
    return attachment;
  }

  if ((typeof attachment === "string" && attachment.startsWith("data:")) || attachment.startsWith("blob:")) {
    try {
      const response = await window.fetch(attachment);
      const blob = await response.blob();
      if (blob) {
        // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
        return new File([blob], fileName, { type: blob.type });
      }
      // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'Blob'.
      return null;
    } catch {
      // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'Blob'.
      return null;
    }
  }

  throw new Error("Failed to parse attachment - not data URI or Blob");
};

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* uploadAttachment(action: IActionWithPayload<string, ITransactionUploadAttachmentPayload>) {
  const {
    transactionID: userTransactionID,
    ticketID,
    agentID,
    accept,
    fileName,
    attachment,
    onProgress,
    localID,
    xhrCallback,
    transactionType,
    cannedResponseID,
  } = action.payload;

  let transactionID = userTransactionID;
  if (!transactionID) {
    yield put(
      TransactionActions.createTransaction({
        ...(ticketID && { ticketID }),
        ...(agentID && { agentID }),
        ...(cannedResponseID && { cannedResponseID }),
        type: transactionType,
      })
    );
    const {
      payload: { transaction, error },
    } = yield take([
      TRANSACTION[CRUDAction.CREATE][RequestAction.SUCCESS],
      TRANSACTION[CRUDAction.CREATE][RequestAction.FAILURE],
    ]);

    if (error) {
      yield put(TransactionActions.createFailure({ error }));
    } else {
      transactionID = transaction.id;
    }
  }

  if (transactionID) {
    // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
    const transactions = yield select(selectTransactions);
    const transaction = transactions[transactionID];

    // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
    const attachmentFile = yield call(parseAttachment, attachment, fileName);
    if (attachmentFile && accept && !accept.includes(attachmentFile.type)) {
      yield put(
        TransactionActions.uploadAttachmentFailure({ transactionID, error: "attachment not in valid format", localID })
      );
      yield put(
        ToastsActions.createToast({
          content: getToastContent(ToastContent.TRANSACTION_ATTACHMENT_NOT_IN_VALID_FORMAT, { accept }),
          variant: ToastVariant.Error,
        })
      );
      return;
    }

    if (!transaction) {
      yield put(
        TransactionActions.uploadAttachmentFailure({ transactionID, error: "transaction not created", localID })
      );
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line no-console
      console.warn("bogus: transaction created but not found on list of transactions");
      return;
    }

    const totalSize = transaction.attachments.reduce(
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (size: any, transactionAttachment: any) => transactionAttachment.size + size,
      0
    );

    if (attachmentFile && attachmentFile.size + totalSize > TRANSACTION_ATTACHMENT_MAX_TOTAL_SIZE) {
      yield put(
        TransactionActions.uploadAttachmentFailure({ transactionID, error: "attachments are too big", localID })
      );
      yield put(
        ToastsActions.createToast({
          content: getToastContent(ToastContent.TRANSACTION_ATTACHMENTS_LIMIT_REACHED),
          variant: ToastVariant.Error,
        })
      );
    } else {
      // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
      const response = yield call(
        // @ts-expect-error TS(2769)
        ApiManager.transactionApi.uploadAttachment,
        attachmentFile,
        transactionID,
        onProgress,
        xhrCallback
      );

      if (response.result) {
        yield put(
          TransactionActions.uploadAttachmentSuccess({
            attachment: TransactionSerializer.deserializeAttachment(response.result[0]),
            transactionID,
            localID,
          })
        );
      } else {
        if (response.responseStatus === 404) {
          yield put(TransactionActions.fetchFailure({ id: transactionID, error: "transaction expired" }));
        }

        yield put(TransactionActions.uploadAttachmentFailure({ transactionID, error: response.error, localID }));
      }
    }
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* deleteAttachment(action: IActionWithPayload<string, ITransactionDeleteAttachmentPayload>) {
  const { transactionID, attachmentID } = action.payload;

  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.transactionApi.deleteAttachment, attachmentID, transactionID);

  if (response.result) {
    yield put(TransactionActions.deleteAttachmentSuccess({ transactionID, attachmentID }));
  } else {
    yield put(TransactionActions.deleteAttachmentFailure({ transactionID, attachmentID }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* create(action: IActionWithPayload<string, ICreateTransactionPayload>) {
  const { ticketID, agentID, cannedResponseID, type } = action.payload;

  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.transactionApi.create, { ticketID, agentID, cannedResponseID, type });

  if (response.result) {
    yield put(TransactionActions.createSuccess(TransactionSerializer.deserialize(response.result)));
  } else {
    yield put(TransactionActions.createFailure({ error: response.error }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* mergeTransaction(action: IActionWithPayload<string, IMergeTransactionPayload>) {
  const {
    transactionID: suppliedTransactionID,
    transactionAgentID: agentID,
    transactionCannedResponseID: cannedResponseID,
    transactionTicketID: ticketID,
    transactionEphemeralID: ephemeralID,
    ...mergePayload
  } = action.payload;

  let transactionID = suppliedTransactionID;
  if (!transactionID) {
    yield put(
      TransactionActions.createTransaction({
        ...(ticketID && { ticketID }),
        ...(agentID && { agentID }),
        ...(cannedResponseID && { cannedResponseID }),
      })
    );

    const {
      payload: { transaction, error },
    } = yield take([
      TRANSACTION[CRUDAction.CREATE][RequestAction.SUCCESS],
      TRANSACTION[CRUDAction.CREATE][RequestAction.FAILURE],
    ]);

    if (error) {
      yield put(TransactionActions.createFailure({ error }));
      yield put(
        TransactionActions.mergeTransactionFailure({
          error: "cannot create transaction",
          // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
          transactionID,
          agentID,
          ephemeralID,
          ticketID,
          cannedResponseID,
        })
      );
      return;
    }
    transactionID = transaction.id;
  }

  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.transactionApi.mergeAttachments, transactionID, mergePayload);

  if (response.result) {
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const attachmentsMerged = response.result.map((at: any) =>
      TransactionSerializer.deserializeAttachment(at, "agent")
    );
    // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
    const inlineImagesList = yield call(getItem, `${transactionID}_inlineImageAttachments`);
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const newInlineImagesList = [...(inlineImagesList || []), ...attachmentsMerged.map((at: any) => at.cid)];
    yield call(saveItem, `${transactionID}_inlineImageAttachments`, JSON.stringify(newInlineImagesList));
    yield put(
      TransactionActions.mergeTransactionSuccess({
        attachments: attachmentsMerged,
        // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
        transactionID,
        ticketID,
        cannedResponseID,
        ephemeralID,
        agentID,
      })
    );
  } else {
    if (response.responseStatus === 404) {
      // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
      yield put(TransactionActions.fetchFailure({ id: transactionID, error: "transaction expired" }));
    }

    yield put(
      TransactionActions.mergeTransactionFailure({
        error: response.error,
        // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
        transactionID,
        ticketID,
        ephemeralID,
        cannedResponseID,
        agentID,
      })
    );
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* fetchTransactionFailure() {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TRANSACTION_NOT_FOUND),
      variant: ToastVariant.Error,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* transactionSagas() {
  yield all([
    takeEvery(TRANSACTION[CRUDAction.FETCH_SINGLE][RequestAction.REQUEST], fetch),
    takeEvery(TRANSACTION.UPLOAD_ATTACHMENT[RequestAction.REQUEST], uploadAttachment),
    takeEvery(TRANSACTION.DELETE_ATTACHMENT[RequestAction.REQUEST], deleteAttachment),
    takeEvery(TRANSACTION.MERGE_TRANSACTION[RequestAction.REQUEST], mergeTransaction),
    takeEvery(TRANSACTION[CRUDAction.FETCH_SINGLE][RequestAction.FAILURE], fetchTransactionFailure),
    takeEvery(TRANSACTION.CREATE[RequestAction.REQUEST], create),
  ]);
}
